/**
 * Which of the two lists the library is showing, and how that rides in the address.
 *
 * KANON `Programy v2 - D3 (0.8)` puts a segmented switch over the master pane: the trainer's own
 * **Szablony** and the plans their clients are actually running, **Plany klientów**. They are two
 * different reads of two different endpoints wearing one list, and the switch is the only thing
 * that says which one is on screen.
 *
 * ── The scope is in the URL and nothing else is ──────────────────────────────────────────────
 * Owner decision, 3 September 2026: `/plans?zakres=klienci`, and **no parameter means the
 * templates**. The filters and the selection stay out of the address.
 *
 * The default is deliberately NOT serialised. `/plans` and `/plans?zakres=szablony` would be two
 * addresses for one screen — the second is what a trainer would paste into a message, and the first
 * is what every link in the app produces, so a bookmark and a nav-rail click would disagree about
 * whether they are the same page. The same rule the builder's `?phase=` follows, for the same
 * reason.
 *
 * ── The key is Polish because the app's addresses are ────────────────────────────────────────
 * `/klienci`, `/klienci/archiwum`, `/plans/nowy`. A URL is something a person reads, and this app
 * reads Polish to its trainers.
 */

/** The two lists. Internal vocabulary — English, like every other type in this slice. */
export type PlanScope = 'templates' | 'clients';

/**
 * The library's search parameters.
 *
 * One key today. The shape is the builder's — an optional member per parameter, absent meaning
 * "not in the URL" — so wave 3's filter facets, if the owner ever moves them into the address,
 * are one entry in `PARSERS` and one field here.
 */
export interface PlanLibrarySearch {
  readonly zakres?: 'klienci';
}

/**
 * One parser per key, so a new parameter is one entry.
 *
 * Returns `undefined` for anything it cannot read, and `undefined` means "not in the URL" — which
 * is how a stale or hand-edited link degrades to the templates instead of to an error page. A
 * bookmark carrying `?zakres=archiwum` is a normal thing to receive.
 */
const PARSERS = {
  zakres: (raw: unknown): 'klienci' | undefined => (raw === 'klienci' ? 'klienci' : undefined),
} satisfies Record<keyof PlanLibrarySearch, (raw: unknown) => unknown>;

/**
 * The route's `validateSearch`.
 *
 * It lives in the slice rather than in `app/routes/`, because which parameters the library
 * understands is the library's decision — a route file is a wiring declaration, and anything a
 * second route could want does not live there.
 */
export function validatePlanLibrarySearch(raw: Record<string, unknown>): PlanLibrarySearch {
  const zakres = PARSERS.zakres(raw.zakres);
  // The key is OMITTED when it parses to nothing rather than set to `undefined`: TanStack Router
  // serialises the object back into the URL, and an explicit `undefined` leaves `?zakres=` behind.
  return zakres === undefined ? {} : { zakres };
}

/** Which list an address is asking for. Anything that is not the clients' plans is the templates. */
export function planScopeOf(search: PlanLibrarySearch): PlanScope {
  return search.zakres === 'klienci' ? 'clients' : 'templates';
}

/** The address a scope should be at. The default serialises to nothing — see the header. */
export function planScopeSearch(scope: PlanScope): PlanLibrarySearch {
  return scope === 'clients' ? { zakres: 'klienci' } : {};
}

/**
 * The i18next `context` this scope contributes to a key.
 *
 * Four call sites read it — the failure's title twice, the live region's row count, and the
 * no-results hint — and every one of them wants "programów" over the templates and the clients'
 * own noun over their plans. `undefined` rather than `'templates'`: i18next falls back to the bare
 * key when the context is absent, which is what makes the templates the default spelling of every
 * one of those sentences rather than a suffix somebody has to remember to add.
 */
export function planScopeContext(scope: PlanScope): 'clients' | undefined {
  return scope === 'clients' ? 'clients' : undefined;
}
